import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, type AuthSession } from "@evocha/api-client";
import type { Agent, User } from "@evocha/shared-types";
import { api } from "./api";

export interface SessionValue {
  user: User | null;
  agent: Agent | null;
  authenticated: boolean;
  /** True while a stored session is being revalidated against the API. */
  loading: boolean;
  signIn: (session: AuthSession) => Promise<void>;
  signOut: () => void;
  refreshSession: () => Promise<void>;
  updateAgent: (agent: Agent) => void;
}

const STORAGE_KEY = "evocha.agent.session";

interface StoredSession {
  access_token: string;
  refresh_token: string;
  user?: User;
  agent?: Agent;
}

export const SessionContext = createContext<SessionValue | null>(null);

function loadSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    return parsed?.access_token ? parsed : null;
  } catch {
    return null;
  }
}

function saveSession(session: StoredSession | null) {
  try {
    if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* private mode */
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<StoredSession | null>(() => loadSession());
  const [loading, setLoading] = useState<boolean>(() => {
    const stored = loadSession();
    return Boolean(stored?.access_token && !stored.user);
  });

  // Revalidate a stored token on boot and hydrate the agent profile.
  useEffect(() => {
    const stored = loadSession();
    if (!stored?.access_token) {
      setLoading(false);
      return;
    }
    let alive = true;
    api
      .me()
      .then((me) => {
        if (!alive) return;
        const next: StoredSession = {
          ...stored,
          user: me.user,
          agent: me.agent,
        };
        saveSession(next);
        setSession(next);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        // Keep the stored credentials on transport/server errors; only a
        // definitive rejection means the session is truly gone.
        if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
          saveSession(null);
          setSession(null);
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const signIn = useCallback(async (next: AuthSession) => {
    const stored: StoredSession = {
      access_token: next.access_token,
      refresh_token: next.refresh_token,
      user: next.user,
      agent: next.agent,
    };
    saveSession(stored);
    setSession(stored);
    setLoading(false);
    queryClient.clear();
  }, [queryClient]);

  const signOut = useCallback(() => {
    // Best effort: drop the refresh token server-side, then clear locally.
    void api.logout().catch(() => undefined);
    saveSession(null);
    setSession(null);
    queryClient.clear();
  }, [queryClient]);

  const refreshSession = useCallback(async () => {
    try {
      const me = await api.me();
      const stored = loadSession();
      if (!stored) {
        setSession(null);
        return;
      }
      const next: StoredSession = { ...stored, user: me.user, agent: me.agent };
      saveSession(next);
      setSession(next);
    } catch {
      saveSession(null);
      setSession(null);
    }
  }, []);

  const updateAgent = useCallback((agent: Agent) => {
    const stored = loadSession();
    if (!stored) return;
    const next: StoredSession = { ...stored, agent };
    saveSession(next);
    setSession(next);
  }, []);

  const value = useMemo<SessionValue>(
    () => ({
      user: session?.user ?? null,
      agent: session?.agent ?? null,
      authenticated: Boolean(session?.access_token),
      loading,
      signIn,
      signOut,
      refreshSession,
      updateAgent,
    }),
    [session, loading, signIn, signOut, refreshSession, updateAgent],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside SessionProvider");
  return ctx;
}
